'use client'

import { useAtom, useAtomValue, useStore } from 'jotai'
import { BotMessageSquareIcon, Maximize2Icon, SquarePenIcon, XIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/navigation'
import { Dialog as SheetPrimitive } from 'radix-ui'

import { Button } from '@/components/ui/button'
import { SheetPortal } from '@/components/ui/sheet'
import { getAssistantPagePath } from '@/features/dashboard/lib/trade-page-path'
import { openAssistant } from '@/features/order/chat/lib/open-assistant'
import { ORDER_FORM_SHEET_WIDTH } from '@/features/order/order-form/components/order-form-sheet'
import { useIsMobileDevice, useIsMobileLayout } from '@/hooks/use-screen'
import { useVisualViewportRect, visualViewportFixedStyle } from '@/hooks/use-visual-viewport'
import {
  assistantPageCarryThreadAtom,
  assistantPageMountedAtom,
  assistantPanelOpenAtom,
} from '@/state/chat'

// Lazy: the chat surface (assistant-ui, markdown, response cards) only renders while the sheet is
// open, so it has no business in every trade page's boot bundle.
const AssistantSurface = dynamic(
  () => import('@/features/assistant/components/assistant-surface').then((m) => m.AssistantSurface),
  { ssr: false }
)

/**
 * The assistant as a right-hand side sheet, the same shell as the popup Order Form and the
 * activity details: full height above the status bar, sliding over the dashboard without
 * reflowing it. Non-modal, so the dashboard stays usable beside it. Hiding it only minimizes:
 * the thread (and any research running in it) carries on, and ⌘K / the sidebar bring it back.
 * Mounted on every /trade route; hidden while /trade/assistant shows the same thread full-page.
 */
export function AssistantPanel() {
  const t = useTranslations('trade.assistant')
  const store = useStore()
  const router = useRouter()
  const [open, setOpen] = useAtom(assistantPanelOpenAtom)
  const pageMounted = useAtomValue(assistantPageMountedAtom)
  const isMobileLayout = useIsMobileLayout()
  // Any touch device, whatever its width: an iPad's keyboard covers the panel just the same.
  const isMobileDevice = useIsMobileDevice()

  // The layout viewport never shrinks for the on-screen keyboard on iOS; track the visual
  // viewport so the composer rises above it (see order-form-sheet.tsx).
  const visualViewportRect = useVisualViewportRect()
  const mobileViewportStyle = isMobileDevice
    ? visualViewportFixedStyle(visualViewportRect)
    : undefined

  const minimize = () => setOpen(false)

  return (
    <SheetPrimitive.Root open={open && !pageMounted} modal={false}>
      <SheetPortal>
        <SheetPrimitive.Content
          aria-describedby={undefined}
          className="sheet-surface data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:animate-in data-[state=open]:slide-in-from-right fixed top-0 right-0 bottom-[var(--status-bar-height,2.5rem)] z-50 flex max-w-dvw flex-col outline-none data-[state=closed]:duration-150 data-[state=closed]:ease-linear data-[state=open]:duration-150 data-[state=open]:ease-linear"
          style={{ width: ORDER_FORM_SHEET_WIDTH, ...mobileViewportStyle }}
          onEscapeKeyDown={(event) => {
            // Root has no onOpenChange (open is driven by the atom), so close explicitly.
            event.preventDefault()
            minimize()
          }}
          // openAssistant focuses the composer itself; don't let Radix grab the first button.
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onFocusOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => event.preventDefault()}
          // Right-clicks inside must not reach the dashboard's page menu.
          onContextMenu={(event) => event.stopPropagation()}
        >
          <div className="border-foreground/10 flex h-11 shrink-0 items-center gap-1 border-b pr-2 pl-4">
            <BotMessageSquareIcon
              className="text-foreground/60 size-4 shrink-0"
              aria-hidden="true"
            />
            <SheetPrimitive.Title className="min-w-0 flex-1 truncate text-sm font-medium">
              {t('title')}
            </SheetPrimitive.Title>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => openAssistant(store, { thread: { kind: 'new' } })}
              aria-label={t('newChat')}
              title={t('newChat')}
            >
              <SquarePenIcon className="size-3.5" />
            </Button>
            {!isMobileLayout && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  // Expand keeps this conversation; the page otherwise opens on a new chat.
                  store.set(assistantPageCarryThreadAtom, true)
                  minimize()
                  router.push(getAssistantPagePath())
                }}
                aria-label={t('expand')}
                title={t('expand')}
              >
                <Maximize2Icon className="size-3.5" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={minimize}
              aria-label={t('minimize')}
              title={t('minimize')}
            >
              <XIcon className="size-4" />
            </Button>
          </div>
          <AssistantSurface />
        </SheetPrimitive.Content>
      </SheetPortal>
    </SheetPrimitive.Root>
  )
}
